import { Navbar, Nav, Container, Offcanvas } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  LuHeart,
  LuShoppingBag,
  LuBell,
  LuUser,
  LuLogOut,
} from "react-icons/lu";
import { useCallback, useEffect, useRef, useState } from "react";
import { RiArrowDropDownLine } from "react-icons/ri";
import { useAuth } from "../context/AuthContext";
import useCatalogNavigation from "../hooks/useCatalogNavigation";
import api from "../api/axios";
import logo from "../images/logo.webp";
import noimage from "../images/noimage.jpg";

import {
  FiHome,
  FiShoppingBag,
  FiHelpCircle,
  FiPhone,
  FiUser,
  FiLogIn,
  FiLogOut,
  FiChevronDown,
  FiChevronRight
} from "react-icons/fi";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user, logout, authLoading } = useAuth();
  const { categories, loading: catalogLoading } = useCatalogNavigation();


  const [showHeader, setShowHeader] = useState(true);
  const lastScrollYRef = useRef(0);
  const scrollFrameRef = useRef(null);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [cartCount, setCartCount] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [wishlistCount, setWishlistCount] = useState(0);

  const userImage = "https://i.pravatar.cc/80?img=47";

  const [showSidebar, setShowSidebar] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [openSubMenu, setOpenSubMenu] = useState(null);
  const [activeMegaCategoryId, setActiveMegaCategoryId] = useState(null);
  const [isShopMegaMenuOpen, setIsShopMegaMenuOpen] = useState(false);

  const activeMegaCategory =
    categories.find(
      (category) =>
        String(category.category_id) === String(activeMegaCategoryId)
    ) || categories[0] || null;

  const handleMobileNavigate = (path, options = {}) => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);

    navigate(path, options);
  };

  const fetchCartCount = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setCartCount(0);
        return;
      }

      const res = await api.get("/customer/cart/count");
      setCartCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Cart count fetch failed:", error);
      setCartCount(0);
    }
  }, [isAuthenticated]);

  const fetchNotificationCount = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setNotificationCount(0);
        return;
      }

      const res = await api.get("/customer/notifications/unread-count");

      setNotificationCount(Number(res.data?.count || 0));
    } catch (error) {
      console.error("Notification count fetch failed:", error);
      setNotificationCount(0);
    }
  }, [isAuthenticated]);

  const fetchWishlistCount = useCallback(async () => {
    try {
      if (!isAuthenticated) {
        setWishlistCount(0);
        return;
      }

      const res = await api.get("/customer/wishlist/count");

      setWishlistCount(
        Number(res.data?.data?.count || res.data?.count || 0)
      );
    } catch (error) {
      console.error("Wishlist count fetch failed:", error);
      setWishlistCount(0);
    }
  }, [isAuthenticated]);

  // Refresh every badge (in parallel) on sign-in changes and route changes.
  useEffect(() => {
    Promise.all([
      fetchCartCount(),
      fetchWishlistCount(),
      fetchNotificationCount(),
    ]);
  }, [
    fetchCartCount,
    fetchWishlistCount,
    fetchNotificationCount,
    location.pathname,
  ]);

  // Long-lived listeners: set up once per sign-in state, not per navigation.
  useEffect(() => {
    const interval = setInterval(fetchNotificationCount, 30000);

    window.addEventListener("notifications-updated", fetchNotificationCount);
    window.addEventListener("cart-updated", fetchCartCount);
    window.addEventListener("wishlist-updated", fetchWishlistCount);

    return () => {
      clearInterval(interval);
      window.removeEventListener("notifications-updated", fetchNotificationCount);
      window.removeEventListener("cart-updated", fetchCartCount);
      window.removeEventListener("wishlist-updated", fetchWishlistCount);
    };
  }, [fetchCartCount, fetchWishlistCount, fetchNotificationCount]);

  useEffect(() => {
    const handleScroll = () => {
      if (scrollFrameRef.current !== null) return;

      scrollFrameRef.current = window.requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        const shouldShow =
          currentScrollY < 50 || currentScrollY < lastScrollYRef.current;

        setShowHeader((currentValue) =>
          currentValue === shouldShow ? currentValue : shouldShow
        );
        lastScrollYRef.current = currentScrollY;
        scrollFrameRef.current = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setSearchKeyword(params.get("search") || "");
  }, [location.search]);

  const handleLogout = async () => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);

    await logout();

    setCartCount(0);
    setNotificationCount(0);
    setWishlistCount(0);

    navigate("/login", {
      replace: true,
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();

    const keyword = searchKeyword.trim();

    if (!keyword) {
      navigate("/shop");
      return;
    }

    navigate(`/shop?search=${encodeURIComponent(keyword)}`);
  };

  const getCategoryPath = (categoryId) =>
    `/shop?category_id=${encodeURIComponent(categoryId)}`;

  const getSubCategoryPath = (categoryId, subCategoryId) =>
    `${getCategoryPath(categoryId)}&sub_category_id=${encodeURIComponent(
      subCategoryId
    )}`;

  const closeShopMegaMenu = () => {
    setIsShopMegaMenuOpen(false);
  };

  const handleShopNavigate = (path) => {
    closeShopMegaMenu();
    navigate(path);
  };

  useEffect(() => {
    setShowSidebar(false);
    setOpenMenu(null);
    setOpenSubMenu(null);
    closeShopMegaMenu();
  }, [location.pathname, location.search]);

  return (
    <div className={`main-header ${showHeader ? "show" : "hide"}`}>
      <div className="topbar">
        <div className="container d-flex justify-content-end px-4 py-1">
          {!authLoading && !isAuthenticated ? (
            <span
              onClick={() => navigate("/login")}
              style={{ cursor: "pointer" }}
            >
              Register or Sign in
            </span>
          ) : (
            <span>Welcome, {user?.name}</span>
          )}

          <span className="px-2">|</span>
          <span>INR</span>
        </div>
      </div>

      <div className="container d-flex align-items-center justify-content-between py-3 mainNav">
        <div onClick={() => navigate("/")} style={{ cursor: "pointer" }} className="ps-5 ps-lg-0">
          <img
            src={logo}
            alt="Wedding Stationery"
            className="logo"
            width="131"
            height="150"
            decoding="async"
          />
        </div>

        <form className="d-flex w-50 order-lg-2" onSubmit={handleSearchSubmit}>
          <input
            type="text"
            placeholder="Search products..."
            className="form-control"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />

          <button type="submit" className="btn btn-dark ms-2">
            Search
          </button>
        </form>

        <div className="d-flex gap-3 align-items-center order-lg-3">
          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/wishlist")}
              style={{
                cursor: "pointer",
                position: "relative",
              }}
            >
              <LuHeart size={20} />

              {wishlistCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-7px",
                    background: "#000",
                    color: "#fff",
                    borderRadius: "999px",
                    minWidth: "16px",
                    height: "16px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </div>
          )}
          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/notifications")}
              style={{
                cursor: "pointer",
                position: "relative",
              }}
            >
              <LuBell size={20} />

              {notificationCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-5px",
                    right: "-7px",
                    background: "#000",
                    color: "#fff",
                    borderRadius: "999px",
                    minWidth: "16px",
                    height: "16px",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {notificationCount > 99 ? "99+" : notificationCount}
                </span>
              )}
            </div>
          )}

          {!authLoading && isAuthenticated && (
            <div
              onClick={() => navigate("/cart")}
              style={{ cursor: "pointer" }}
              className="d-flex align-items-center gap-1 position-relative"
            >
              <LuShoppingBag size={20} />
              <span
                style={{
                  position: "absolute",
                  top: "-8px",
                  right: "-10px",
                  background: "#000",
                  color: "#fff",
                  borderRadius: "999px",
                  minWidth: "16px",
                  height: "16px",
                  fontSize: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 4px",
                }}
              >
                {cartCount}
              </span>
            </div>
          )}

          {!authLoading && !isAuthenticated ? (
            // <button
            //   type="button"
            //   className="login-header-btn"
            //   onClick={() => navigate("/login")}
            // >
            //   Login
            // </button>
            ''
          ) : (
            !authLoading &&
            isAuthenticated && (
              <div className="user-menu">
                <div className="user-trigger">
                  <img
                    src={user?.image_url || userImage}
                    alt="user"
                    className="user-avatar"
                  />

                  <div className="user-info">
                    <strong>
                      {user?.name?.length > 7
                        ? user.name.substring(0, 7) + "..."
                        : user?.name}
                    </strong>
                  </div>

                  <RiArrowDropDownLine size={22} />
                </div>

                <div className="user-dropdown">
                  {/* <div className="user-head">
                    <img
                      src={user?.image_url || userImage}
                      alt="user"
                      className="dropdown-avatar"
                    />

                    <div>
                      <h6>{user?.name || userName}</h6>
                      <p>{user?.email || userEmail}</p>
                    </div>
                  </div>

                  <div className="dropdown-line" /> */}

                  <div className="user-drop-item" onClick={() => navigate("/profile")}>
                    <LuUser />
                    <span>My Account</span>
                  </div>

                  <div className="user-drop-item" onClick={() => navigate("/orders")}>
                    <LuShoppingBag />
                    <span>My Orders</span>
                  </div>

                  <div className="user-drop-item" onClick={() => navigate("/disputes")}>
                    <FiHelpCircle />
                    <span>Help Center</span>
                  </div>

                  <div className="dropdown-line" />

                  <div className="user-drop-item logout" onClick={handleLogout}>
                    <LuLogOut />
                    <span>Log Out</span>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      <Navbar expand="lg" className="border-lg-top border-lg-bottom resphide_height catalog-navbar">
        <Container>
          <Navbar.Toggle onClick={() => setShowSidebar(true)} />

          <Navbar.Collapse className="justify-content-center resphide">
            <Nav className="gap-4 text-uppercase fw-semibold">
              <Nav.Link
                onClick={() => navigate("/")}
                className={location.pathname === "/" ? "active-menu" : ""}
              >
                Home
              </Nav.Link>

              <div
                data-testid="shop-mega-menu"
                className={`shop-mega-menu ${
                  isShopMegaMenuOpen ? "is-open" : ""
                }`}
                onMouseEnter={() => setIsShopMegaMenuOpen(true)}
                onMouseLeave={closeShopMegaMenu}
                onFocusCapture={() => setIsShopMegaMenuOpen(true)}
                onBlurCapture={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) {
                    closeShopMegaMenu();
                  }
                }}
              >
                <button
                  type="button"
                  className={`nav-link shop-menu-trigger ${
                    location.pathname === "/shop" ? "active-menu" : ""
                  }`}
                  onClick={() => handleShopNavigate("/shop")}
                  aria-haspopup="true"
                  aria-expanded={isShopMegaMenuOpen}
                >
                  Shop <FiChevronDown aria-hidden="true" />
                </button>

                <div className="shop-mega-panel" aria-label="Shop categories">
                  {catalogLoading ? (
                    <p className="shop-mega-status">Loading categories...</p>
                  ) : activeMegaCategory ? (
                    <div className="shop-mega-layout">
                      <nav
                        className="shop-mega-category-nav"
                        aria-label="Product categories"
                      >
                        <span className="shop-mega-kicker">Shop</span>
                        {/* <Link to="/shop" className="shop-mega-shop-all">
                          Shop all
                        </Link> */}

                        <div className="shop-mega-category-list">
                          {categories.map((category) => (
                            <Link
                              key={category.category_id}
                              to={getCategoryPath(category.category_id)}
                              className={
                                String(activeMegaCategory.category_id) ===
                                String(category.category_id)
                                  ? "is-active"
                                  : ""
                              }
                              onMouseEnter={() =>
                                setActiveMegaCategoryId(category.category_id)
                              }
                              onFocus={() =>
                                setActiveMegaCategoryId(category.category_id)
                              }
                              onClick={closeShopMegaMenu}
                            >
                              <span>{category.name}</span>
                              <FiChevronRight aria-hidden="true" />
                            </Link>
                          ))}
                        </div>
                      </nav>

                      <section className="shop-mega-subcategory-panel">
                        <div className="shop-mega-section-heading">
                          <div>
                            <span>Explore</span>
                            {/* <h2>{activeMegaCategory.name}</h2> */}
                          </div>
                          <Link
                            to={getCategoryPath(
                              activeMegaCategory.category_id
                            )}
                            onClick={closeShopMegaMenu}
                          >
                            View category
                          </Link>
                        </div>

                        {Array.isArray(
                          activeMegaCategory.sub_categories
                        ) &&
                        activeMegaCategory.sub_categories.length > 0 ? (
                          <div className="shop-mega-subcategory-grid">
                            {activeMegaCategory.sub_categories.map(
                              (subCategory) => (
                                <Link
                                  key={subCategory.sub_category_id}
                                  to={getSubCategoryPath(
                                    activeMegaCategory.category_id,
                                    subCategory.sub_category_id
                                  )}
                                  aria-label={subCategory.name}
                                  onClick={closeShopMegaMenu}
                                >
                                  <span className="shop-mega-subcategory-mark">
                                    {subCategory.name?.charAt(0) || "S"}
                                  </span>
                                  <span>{subCategory.name}</span>
                                </Link>
                              )
                            )}
                          </div>
                        ) : (
                          <div className="shop-mega-empty-subcategories">
                            <p>Explore every style in this category.</p>
                            <Link
                              to={getCategoryPath(
                                activeMegaCategory.category_id
                              )}
                              onClick={closeShopMegaMenu}
                            >
                              Browse {activeMegaCategory.name}
                            </Link>
                          </div>
                        )}
                      </section>

                      <Link
                        to={getCategoryPath(activeMegaCategory.category_id)}
                        className="shop-mega-feature"
                        aria-label={`Shop ${activeMegaCategory.name}`}
                        onClick={closeShopMegaMenu}
                      >
                        <img
                          src={activeMegaCategory.image_url || noimage}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          onError={(event) => {
                            event.currentTarget.src = noimage;
                          }}
                        />
                        <span className="shop-mega-feature-shade" />
                        <div className="shop-mega-feature-content">
                          <span>Featured category</span>
                          <h3>{activeMegaCategory.name}</h3>
                          <strong>
                            Shop now <FiChevronRight aria-hidden="true" />
                          </strong>
                        </div>
                      </Link>
                    </div>
                  ) : (
                    <p className="shop-mega-status">
                      Categories are currently unavailable.
                    </p>
                  )}
                </div>
              </div>

              {/* <NavDropdown
                title={
                  <span className="d-flex align-items-center">
                    Shop Wedding <RiArrowDropDownLine size={20} />
                  </span>
                }
                className={`custom-dropdown ${location.pathname.includes("/shop")
                  ? "active-menu"
                  : ""
                  }`}
              >
                <div className="dropdown-item-wrapper">
                  <span onClick={() => navigate("/shop")}>
                    Wedding Cards
                  </span>
                </div>

                <div className="dropdown-item-wrapper has-child">
                  <span className="d-flex align-items-center">
                    By Occasion <MdOutlineNavigateNext />
                  </span>

                  <div className="submenu">
                    <div onClick={() => navigate("/shop-wedding/hindu")}>
                      Hindu Wedding
                    </div>

                    <div onClick={() => navigate("/shop-wedding/muslim")}>
                      Muslim Wedding
                    </div>

                    <div onClick={() => navigate("/shop-wedding/christian")}>
                      Christian Wedding
                    </div>
                  </div>
                </div>
              </NavDropdown> */}

              {/* <Nav.Link
                onClick={() => navigate("/about-us")}
                className={
                  location.pathname === "/about-us" ? "active-menu" : ""
                }
              >
                About Us
              </Nav.Link> */}

              {/* <Nav.Link
                onClick={() => navigate("/srishbish-mini")}
                className={
                  location.pathname === "/srishbish-mini" ? "active-menu" : ""
                }
              >
                Srishbish Mini
              </Nav.Link> */}

              <Nav.Link
                onClick={() => navigate("/faqs")}
                className={location.pathname === "/faqs" ? "active-menu" : ""}
              >
                FAQs
              </Nav.Link>



              <Nav.Link
                onClick={() => navigate("/contact-us")}
                className={
                  location.pathname === "/contact-us" ? "active-menu" : ""
                }
              >
                Contact Us
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Offcanvas
        show={showSidebar}
        onHide={() => setShowSidebar(false)}
        placement="start"
      >
        <Offcanvas.Header closeButton>
          <div onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
            <img
              src={logo}
              alt="Wedding Stationery"
              className="logo"
              width="131"
              height="150"
              decoding="async"
            />
          </div>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="mobile-sidebar-menu">

            <div onClick={() => handleMobileNavigate("/")}>
              <FiHome className="me-2" /> Home
            </div>

            <button
              type="button"
              className="mobile-sidebar-toggle"
              onClick={() => {
                setOpenMenu((current) =>
                  current === "shop" ? null : "shop"
                );
                setOpenSubMenu(null);
              }}
              aria-expanded={openMenu === "shop"}
            >
              <span>
                <FiShoppingBag className="me-2" /> Shop
              </span>
              {openMenu === "shop" ? <FiChevronDown /> : <FiChevronRight />}
            </button>

            {openMenu === "shop" && (
              <div className="mobile-catalog-menu">
                <button
                  type="button"
                  className="mobile-view-all"
                  onClick={() => handleMobileNavigate("/shop")}
                >
                  View all products
                </button>

                {catalogLoading ? (
                  <p className="mobile-catalog-status">Loading categories...</p>
                ) : (
                  categories.map((category) => {
                    const menuKey = String(category.category_id);
                    const subCategories = Array.isArray(
                      category.sub_categories
                    )
                      ? category.sub_categories
                      : [];

                    return (
                      <div className="mobile-category-group" key={menuKey}>
                        <div className="mobile-category-row">
                          <button
                            type="button"
                            className="mobile-category-link"
                            onClick={() =>
                              handleMobileNavigate(
                                getCategoryPath(category.category_id)
                              )
                            }
                          >
                            <img
                              src={category.image_url || noimage}
                              alt=""
                              onError={(event) => {
                                event.currentTarget.src = noimage;
                              }}
                            />
                            <span>{category.name}</span>
                          </button>

                          {subCategories.length > 0 && (
                            <button
                              type="button"
                              className="mobile-subcategory-toggle"
                              onClick={() =>
                                setOpenSubMenu((current) =>
                                  current === menuKey ? null : menuKey
                                )
                              }
                              aria-label={`Show ${category.name} subcategories`}
                              aria-expanded={openSubMenu === menuKey}
                            >
                              {openSubMenu === menuKey ? (
                                <FiChevronDown />
                              ) : (
                                <FiChevronRight />
                              )}
                            </button>
                          )}
                        </div>

                        {openSubMenu === menuKey && (
                          <div className="mobile-subcategory-list">
                            {subCategories.map((subCategory) => (
                              <button
                                type="button"
                                key={subCategory.sub_category_id}
                                onClick={() =>
                                  handleMobileNavigate(
                                    getSubCategoryPath(
                                      category.category_id,
                                      subCategory.sub_category_id
                                    )
                                  )
                                }
                              >
                                {subCategory.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}

            {/* <div onClick={() => handleMobileNavigate("/about-us")}>
              <FiInfo className="me-2" /> About Us
            </div> */}

            <div onClick={() => handleMobileNavigate("/faqs")}>
              <FiHelpCircle className="me-2" /> FAQs
            </div>

            <div onClick={() => handleMobileNavigate("/contact-us")}>
              <FiPhone className="me-2" /> Contact Us
            </div>

            <hr />

            {!authLoading && !isAuthenticated ? (
              <div onClick={() => handleMobileNavigate("/login")}>
                <FiLogIn className="me-2" /> Login
              </div>
            ) : (
              <>
                <div onClick={() => handleMobileNavigate("/profile")}>
                  <FiUser className="me-2" /> My Account
                </div>

                <div onClick={() => handleMobileNavigate("/orders")}>
                  <FiShoppingBag className="me-2" /> My Orders
                </div>

                <div onClick={() => handleMobileNavigate("/disputes")}>
                  <FiHelpCircle className="me-2" /> Help Center
                </div>

                <div
                  onClick={async () => {
                    setShowSidebar(false);
                    setOpenMenu(null);
                    setOpenSubMenu(null);
                    await handleLogout();
                  }}
                >
                  <FiLogOut className="me-2" /> Logout
                </div>
              </>
            )}
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </div>
  );
};

export default Header;
